import type { Questionnaire } from "@medplum/fhirtypes";
import { catalogModelKindOf, PERFORMANCE_MEASURE_DEFINITION_KIND, QUESTIONNAIRE_DEFINITION_PROFILE } from "@repo/api";
import { CATALOG_VIEW_SCOPE } from "./catalog-view-scope";
import {
  readPerformanceMeasureDefinitionRow,
  readQuestionnaireDefinitionRow,
} from "./questionnaire-definition-storage";
import type { QuestionnaireDerived } from "./types";

/**
 * Project the Questionnaire Definition discriminator used by its views.
 *
 * A questionnaire definition row is also READ IN FULL here and the result
 * discarded. Nothing below needs it; the point is where the failure lands. The
 * projection runs inside a TanStack DB `fn.select`, where a throw blanks the
 * whole page, while a throw here is caught by `deriveForSync` in `./base.ts`,
 * which logs the resource id and drops that one row. Validating at sync time
 * is what stops an unreadable row ever reaching the live query.
 */
export function deriveQuestionnaireFields(definition: Questionnaire): QuestionnaireDerived {
  const catalogKind = catalogModelKindOf(definition);
  const questionnaireDefinition = definition.meta?.profile?.includes(QUESTIONNAIRE_DEFINITION_PROFILE) ?? false;
  if (catalogKind === PERFORMANCE_MEASURE_DEFINITION_KIND) readPerformanceMeasureDefinitionRow(definition);
  if (questionnaireDefinition) readQuestionnaireDefinitionRow(definition);
  const definitionRef = definition.url && definition.version ? `${definition.url}|${definition.version}` : undefined;
  return {
    catalogViewScope: CATALOG_VIEW_SCOPE,
    catalogModelKind: catalogKind,
    catalogVersion: Number(definition.version),
    catalogLineage: definition.url ?? definition.name ?? definition.id ?? "",
    questionnaireDefinition,
    questionnaireDefinitionRef: questionnaireDefinition ? definitionRef : undefined,
  };
}
